import { Component, ChangeDetectionStrategy, computed, inject, input, signal } from '@angular/core';
import { AttrPath } from '@cumlaude/metadata';
import { Entry, EnumService, LegendaEnumNames } from '../core/services/enum.service';
import { generateCssClassForCohortdetails } from '../services/css-generate-metadata';
import { generateCssClassForString } from '@cumlaude/shared-utils';
import { DisplayService } from '../services/display.service';
import { NgClickOutsideDelayOutsideDirective } from 'ng-click-outside2';
import { ButtonComponent } from '@cumlaude/shared-components-buttons';
import { IconComponent } from '@cumlaude/shared-components-icons';
import type { IconName } from '@cumlaude/shared-components-icons';

export interface LegendaEntry extends Entry {
	class: string;
	icon?: IconName;
	rotate?: 90 | 180 | 270;
}

export type LegendaStyle = 'PILL';

@Component({
	selector: 'app-legenda',
	templateUrl: './legenda.component.html',
	styleUrls: ['./legenda.component.scss'],
	imports: [ButtonComponent, NgClickOutsideDelayOutsideDirective, IconComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LegendaComponent {
	private readonly displayService = inject(DisplayService);

	attribute = input.required<AttrPath | [LegendaEnumNames]>();

	keyIsClassName = input(false);

	includeNull = input(false);

	exclude = input<string[]>([]);

	style = input<LegendaStyle | undefined>(undefined);

	open = signal(false);

	options = computed(() => {
		const entries = EnumService.legendaEnumEntries(this.attribute())
			.filter((entry) => !this.exclude().includes(entry.value))
			.map((entry) => ({ ...entry, class: this.getClass(entry), ...this.getIcon(entry) }));
		return [...entries, ...this.getNullEntry()];
	});

	private getIcon({ key }: Entry): Pick<LegendaEntry, 'icon' | 'rotate'> {
		const legendName = Array.isArray(this.attribute()) ? this.attribute()[0] : undefined;
		if (legendName !== 'x_onderwijsresultaten_actueel') return {};

		switch (key) {
			case 'BovenDeNormWaarde':
				return { icon: 'toevoegen' };
			case 'OnderDeNormWaarde':
				return { icon: 'minus' };
			case 'StijgingVorigJaar':
				return { icon: 'pijl-links-beneden', rotate: 270 };
			case 'DalingVorigJaar':
				return { icon: 'pijl-links-beneden' };
			case 'DalingVorige2Jaren':
				return { icon: 'pijl-links-beneden' };
			default:
				return {};
		}
	}

	getClass({ key, value }: Entry) {
		if (this.style() === 'PILL') {
			const { className } = generateCssClassForCohortdetails(value);
			return `${className} pill`;
		}
		return `${generateCssClassForString(this.keyIsClassName() ? key : value)}`;
	}

	private getNullEntry(): LegendaEntry[] {
		if (!this.includeNull()) return [];

		const nullValue = this.displayService.displayNullValue(this.attribute());
		return [{ key: null, value: nullValue, class: this.getClass({ key: null, value: nullValue }) }];
	}
}
